import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { X, Key } from "lucide-react";
import { governanceService } from "@/services/governance.service";
import type { PermissionItem } from "@/types";
import toast from "react-hot-toast";

const PERMISSION_GROUPS = [
  "Products",
  "Customers",
  "Suppliers",
  "Invoices",
  "Procurement",
  "Reports",
  "Settings",
  "Users",
  "Compliance",
  "Loyalty",
  "Prescriptions",
  "General",
] as const;

interface PermissionFormModalProps {
  permission?: PermissionItem | null;
  onClose: () => void;
}

export function PermissionFormModal({
  permission,
  onClose,
}: PermissionFormModalProps) {
  const queryClient = useQueryClient();
  const isEditing = Boolean(permission);

  const [key, setKey] = useState(permission?.key ?? "");
  const [group, setGroup] = useState(permission?.group ?? "General");
  const [description, setDescription] = useState(permission?.description ?? "");

  const { mutateAsync: createPermission, isPending: isCreating } = useMutation({
    mutationFn: (payload: {
      key: string;
      group: string;
      description?: string;
    }) => governanceService.createPermission(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-permissions"] });
      toast.success("Permission created successfully");
      onClose();
    },
    onError: (error: any) => {
      toast.error(
        error.response?.data?.message || "Failed to create permission",
      );
    },
  });

  const { mutateAsync: updatePermission, isPending: isUpdating } = useMutation({
    mutationFn: (payload: {
      key: string;
      group: string;
      description?: string;
    }) => governanceService.updatePermission(permission!.id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-permissions"] });
      queryClient.invalidateQueries({ queryKey: ["admin-roles"] });
      toast.success("Permission updated successfully");
      onClose();
    },
    onError: (error: any) => {
      toast.error(
        error.response?.data?.message || "Failed to update permission",
      );
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!key.trim()) {
      toast.error("Permission key is required");
      return;
    }

    // Validate key format (resource:action)
    const keyPattern = /^[a-z]+:[a-z]+$/;
    if (!keyPattern.test(key.trim())) {
      toast.error(
        "Key must be in format: resource:action (e.g., products:create)",
      );
      return;
    }

    const payload = {
      key: key.trim().toLowerCase(),
      group: group,
      description: description.trim() || undefined,
    };

    if (isEditing) {
      await updatePermission(payload);
    } else {
      await createPermission(payload);
    }
  };

  const isPending = isCreating || isUpdating;

  // Auto-suggest group based on key prefix
  const handleKeyChange = (value: string) => {
    setKey(value.toLowerCase());
    const prefix = value.split(":")[0];
    if (prefix) {
      const matchingGroup = PERMISSION_GROUPS.find(
        (g) => g.toLowerCase() === prefix.toLowerCase(),
      );
      if (matchingGroup) {
        setGroup(matchingGroup);
      }
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative w-full max-w-lg bg-white rounded-3xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-8 py-6 border-b border-slate-100">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-2xl bg-cyan-50 text-cyan-600 border border-cyan-100">
              <Key className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-xl font-black text-slate-900 tracking-tight">
                {isEditing ? "Edit Permission" : "Create Permission"}
              </h2>
              <p className="text-xs text-slate-500 font-medium mt-0.5">
                {isEditing
                  ? "Modify permission key configuration"
                  : "Define a new permission key"}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-xl text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-all"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="px-8 py-6 space-y-5">
          <div className="space-y-2">
            <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400">
              Permission Key *
            </label>
            <input
              type="text"
              value={key}
              onChange={(e) => handleKeyChange(e.target.value)}
              placeholder="e.g., products:create"
              className="w-full h-12 px-4 rounded-xl border border-slate-200 text-sm font-mono font-medium text-slate-900 placeholder-slate-400 focus:border-blue-500 focus:outline-none transition-colors"
              disabled={isEditing}
            />
            <p className="text-[10px] text-slate-400 font-medium">
              Format: resource:action (e.g., products:create, customers:delete)
            </p>
          </div>

          <div className="space-y-2">
            <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400">
              Group
            </label>
            <select
              value={group}
              onChange={(e) => setGroup(e.target.value)}
              className="w-full h-12 px-4 rounded-xl border border-slate-200 text-sm font-medium text-slate-700 bg-white focus:border-blue-500 focus:outline-none transition-colors"
            >
              {PERMISSION_GROUPS.map((g) => (
                <option key={g} value={g}>
                  {g}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-2">
            <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400">
              Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Brief description of what this permission allows..."
              rows={2}
              className="w-full px-4 py-3 rounded-xl border border-slate-200 text-sm font-medium text-slate-900 placeholder-slate-400 focus:border-blue-500 focus:outline-none transition-colors resize-none"
            />
          </div>

          {/* Preview */}
          <div className="p-4 rounded-xl bg-slate-50 border border-slate-100">
            <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2">
              Preview
            </p>
            <div className="flex items-center gap-3">
              <code className="px-3 py-1.5 rounded-lg bg-blue-50 text-blue-600 text-xs font-bold border border-blue-100">
                {key || "resource:action"}
              </code>
              <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">
                in
              </span>
              <span className="px-2 py-1 rounded-lg bg-slate-100 text-slate-600 text-[10px] font-bold uppercase tracking-widest">
                {group}
              </span>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2.5 rounded-xl text-xs font-black uppercase tracking-widest text-slate-600 hover:bg-slate-100 transition-all"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isPending}
              className="px-6 py-2.5 rounded-xl bg-blue-600 text-white text-xs font-black uppercase tracking-widest hover:bg-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-blue-500/20"
            >
              {isPending
                ? "Saving..."
                : isEditing
                  ? "Update Permission"
                  : "Create Permission"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
